import React, { useState, useEffect } from "react";
import axios from "axios";
import { useSnackbar } from "notistack";
import { useHistory, useLocation, Redirect } from 'react-router-dom';
import { LocalizationIds } from "helpers/Localizations";
import { useIntl } from "react-intl";
import moment from "moment";


interface ICustomersContext {
    recordCount: number
    search: ISearchRequest
    customers: ICustomer[]
    isLoading: boolean

    doSearch: (search: ISearchRequest) => void
    handleChangePage: (page: number) => void
    searchCustomers: (searchRequest: ISearchRequest) => Promise<void>
    deleteCustomer: (customerId: string) => Promise<void>
    restoreUser: (customerId: string) => Promise<void>
    updateSearch: (request: ISearchRequest) => void
    reSendEmail: (userEmail: string) => Promise<void>
    searchCustomerCompanies: (searchtext: string, etag: number) => Promise<any>
}

export interface ICustomer {
    id: string
    email: string
    fullName: string
    isDeleted: boolean
    customerId: number
    customerName: string
    createdOn: moment.Moment
}

interface ISearchRequest {
    name: string
    email: string
    customerId: number
    customerName: string
    showOnly: 'active' | 'deleted'
    pageNr: number
    pageSize: number
}

export let customersContext = React.createContext({} as ICustomersContext);

let { Provider } = customersContext;

let CustomersProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    let { enqueueSnackbar } = useSnackbar();
    let history = useHistory();
    let location = useLocation();
    let { formatMessage } = useIntl();

    let [isLoading, setLoading] = useState(true);

    let [customers, setCustomers] = useState<ICustomer[]>([]);
    let [recordCount, setTotalRecords] = useState(0);

    let [search, setSearch] = useState<ISearchRequest>({
        name: '',
        email: '',
        customerId: 0,
        customerName: '',
        showOnly: 'active',
        pageNr: 0,
        pageSize: 15
    });

    useEffect(() => {
        if (location.state)
            processState(location.state);
    }, [location.state])

    if (!location.state)
        return <Redirect to={{ pathname: location.pathname, state: { search } }} />


    let processState = (state: any) => {
        if (state.search) {
            setSearch(state.search);
            searchCustomers(state.search);
        }
    }

    let doSearch = (values: ISearchRequest) => {
        let request = {
            ...values,
            pageNr: values.pageNr > 0 ? 0 : values.pageNr
        }

        history.push(location.pathname, { search: request });
    }

    let searchCustomers = async (searchRequest: ISearchRequest) => {
        try {
            setLoading(true);
            let response = await axios.post('/api/Customer/SearchCustomers', searchRequest);
            setCustomers(response.data.items);
            setTotalRecords(response.data.recordCount);
        } catch (error) { }
        finally {
            setLoading(false);
        }
    }


    let searchCustomerCompanies = async (searchText: string, etag: number) => {
        if (!searchText)
            return;

        try {
            const res = await axios.post('/api/Order/CustomersQuickSearch', { searchText, etag });
            return res.data;
        } catch { }
    }


    let handleChangePage = (page: number) => {
        let request = {
            ...search,
            pageNr: page
        }

        window.scrollTo(0, 0);
        history.push(location.pathname, { search: request });
    }

    let deleteCustomer = async (customerId: string) => {
        if (window.confirm(formatMessage({ id: LocalizationIds.DeleteUserConfirm }))) {
            try {
                await axios.delete(`/api/Customer/DeleteCustomer/${customerId}`)
                setCustomers(customers.filter(customer => customer.id !== customerId));
                setTotalRecords(recordCount - 1);
                enqueueSnackbar("Customer deleted", { variant: 'success' });
            } catch (error) { }
        }
    }

    let restoreUser = async (userId: string) => {
        if (window.confirm(formatMessage({ id: LocalizationIds.INFO_RestoreUserConfirm })) === false)
            return;

        try {
            await axios.post('/api/Customer/RestoreCustomer', { userId });
            setCustomers(customers.filter(user => user.id !== userId));
            setTotalRecords(recordCount - 1);
            enqueueSnackbar("User restored", { variant: "success" });

        } catch (err) { }
    }

    let updateSearch = (request: ISearchRequest) => {
        setSearch(request);
    }

    let reSendEmail = async (userEmail: string) => {
        if (window.confirm(formatMessage({ id: LocalizationIds.ResendEmailInvitationConfirm })) === false)
            return;

        try {
            await axios.get(`/api/Customer/SendInvite/${userEmail}`)
            enqueueSnackbar("Customer invited", { variant: 'success' });
        } catch (error) { }
    }

    return (
        <Provider value={{
            recordCount,
            search,
            customers,
            isLoading,

            doSearch,
            handleChangePage,
            searchCustomers,
            deleteCustomer,
            updateSearch,
            reSendEmail,
            restoreUser,
            searchCustomerCompanies
        }}>
            {children}
        </Provider>
    );
};

export default CustomersProvider;